import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  Sun, MapPin, Clock, Calendar, Users, Menu, X,
  ChevronRight, Loader2, Plane, Check, ArrowRight, Map
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Google Maps Places Autocomplete for location search
const searchLocationsGoogle = (query, callback) => {
  if (!query || query.length < 2) {
    callback([]);
    return;
  }
  
  // Check if Google Maps is loaded
  if (!window.google || !window.google.maps || !window.google.maps.places) {
    console.error('Google Maps not loaded, falling back to OpenStreetMap');
    // Fallback to OpenStreetMap
    searchLocations(query).then(callback).catch(() => callback([]));
    return;
  }
  
  try {
    const service = new window.google.maps.places.AutocompleteService();
    
    service.getPlacePredictions(
      {
        input: query,
        componentRestrictions: { country: 'gr' }, // Restrict to Greece
        location: new window.google.maps.LatLng(36.3932, 25.4615), // Santorini center
        radius: 50000, // 50km radius
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const results = predictions.map(prediction => ({
            name: prediction.structured_formatting.main_text,
            fullName: prediction.description,
            placeId: prediction.place_id
          }));
          callback(results);
        } else {
          console.error('Google Maps Places API error:', status);
          // Fallback to OpenStreetMap on error
          searchLocations(query).then(callback).catch(() => callback([]));
        }
      }
    );
  } catch (error) {
    console.error('Error with Google Maps autocomplete:', error);
    // Fallback to OpenStreetMap
    searchLocations(query).then(callback).catch(() => callback([]));
  }
};

// Fallback to OpenStreetMap if Google Maps not loaded
const searchLocations = async (query) => {
  if (!query || query.length < 2) return [];
  
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?` + 
      new URLSearchParams({
        q: query + ' Santorini',
        format: 'json',
        addressdetails: '1',
        limit: '8',
        'accept-language': 'en'
      }),
      { headers: { 'User-Agent': 'Santorini-Best-Experience' } }
    );
    
    const data = await response.json();
    return data.map(place => ({
      name: place.display_name.split(',').slice(0, 2).join(', '),
      fullName: place.display_name,
      lat: parseFloat(place.lat),
      lng: parseFloat(place.lon)
    }));
  } catch (error) {
    console.error('Location search error:', error);
    return [];
  }
};

const VEHICLES = [
  { 
    type: 'van', 
    name: 'Van', 
    passengers: 8, 
    tourRate: 65, 
    transferBase: 38, 
    icon: '🚐', 
    desc: 'Mercedes Vito / Nissan NV200 (1-8 passengers)',
    image: 'https://images.unsplash.com/photo-1765461734605-34657fa04db2?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
];

export default function BookingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Check if coming from tour link (skip transfer option)
  const bookingTypeParam = searchParams.get('type');
  const pickupParam = searchParams.get('pickup');
  const promoParam = (searchParams.get('promo') || '').toUpperCase();
  const isTourOnly = bookingTypeParam === 'tour';
  const [promoCode] = useState(promoParam);
  
  // Customer info
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState(36); // Store calculated price
  const [paymentType, setPaymentType] = useState('deposit'); // 'deposit' or 'full'
  
  // Booking form state
  const [step, setStep] = useState(isTourOnly ? 2 : 1); // Skip to vehicle selection if tour only
  const [bookingType, setBookingType] = useState(isTourOnly ? 'tour' : 'transfer');
  const [selectedVehicle, setSelectedVehicle] = useState('van');
  const [passengers, setPassengers] = useState(1);
  const [pickupLocation, setPickupLocation] = useState('');
  const [pickupCoords, setPickupCoords] = useState({ 
    lat: pickupParam === 'port' ? 36.3897 : pickupParam === 'airport' ? 36.3993 : 36.3932, 
    lng: pickupParam === 'port' ? 25.4303 : pickupParam === 'airport' ? 25.4793 : 25.4615 
  });
  const [destination, setDestination] = useState('');
  const [destCoords, setDestCoords] = useState({ lat: 0, lng: 0 });
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [hours, setHours] = useState(3);  // Minimum 3 hours for tours
  const [notes, setNotes] = useState('');
  
  // Round-trip state
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [returnPickupLocation, setReturnPickupLocation] = useState('');
  const [returnPickupCoords, setReturnPickupCoords] = useState({ lat: 0, lng: 0 });
  const [returnDestination, setReturnDestination] = useState('');
  const [returnDestCoords, setReturnDestCoords] = useState({ lat: 0, lng: 0 });
  
  // Location search
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [destSuggestions, setDestSuggestions] = useState([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDestSuggestions, setShowDestSuggestions] = useState(false);
  const [searchingPickup, setSearchingPickup] = useState(false);
  const [searchingDest, setSearchingDest] = useState(false);
  const pickupTimeout = useRef(null);
  const destTimeout = useRef(null);
  
  // Return trip location search
  const [returnPickupSuggestions, setReturnPickupSuggestions] = useState([]);
  const [returnDestSuggestions, setReturnDestSuggestions] = useState([]);
  const [showReturnPickupSuggestions, setShowReturnPickupSuggestions] = useState(false);
  const [showReturnDestSuggestions, setShowReturnDestSuggestions] = useState(false);
  const [searchingReturnPickup, setSearchingReturnPickup] = useState(false);
  const [searchingReturnDest, setSearchingReturnDest] = useState(false);
  const returnPickupTimeout = useRef(null);
  const returnDestTimeout = useRef(null);
  
  // Map state
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Calculate price when locations change
  useEffect(() => {
    const updatePrice = async () => {
      const price = await calculateEstimate();
      setEstimatedPrice(price);
    };
    updatePrice();
  }, [pickupCoords, destCoords, returnPickupCoords, returnDestCoords, selectedVehicle, passengers, bookingType, hours, isRoundTrip, pickupDate]);

  // Auto-fill pickup location based on URL parameter
  useEffect(() => {
    if (pickupParam === 'port') {
      setPickupLocation('Ormos Athinios, Megalochori');
    } else if (pickupParam === 'airport') {
      setPickupLocation('Santorini Airport (JTR)');
    }
  }, [pickupParam]);

  // Initialize and update Google Map
  useEffect(() => {
    if (!window.google || !window.google.maps || bookingType !== 'transfer') return;
    
    // Only render map if we have both pickup and destination
    if (!pickupCoords.lat || !destCoords.lat || !destination || !mapRef.current) return;
    
    // Initialize map
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: pickupCoords.lat, lng: pickupCoords.lng },
      zoom: 12,
      styles: [
        { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
        {
          featureType: "administrative.locality",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "road",
          elementType: "geometry",
          stylers: [{ color: "#38414e" }],
        },
        {
          featureType: "road",
          elementType: "geometry.stroke",
          stylers: [{ color: "#212a37" }],
        },
        {
          featureType: "road",
          elementType: "labels.text.fill",
          stylers: [{ color: "#9ca5b3" }],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [{ color: "#17263c" }],
        },
      ],
    });
    
    // Add pickup marker
    new window.google.maps.Marker({
      position: { lat: pickupCoords.lat, lng: pickupCoords.lng },
      map: map,
      title: "Pickup",
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: "#f59e0b",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 2,
      },
    });
    
    // Add destination marker
    new window.google.maps.Marker({
      position: { lat: destCoords.lat, lng: destCoords.lng },
      map: map,
      title: "Destination",
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: "#06b6d4",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 2,
      },
    });
    
    // Draw route
    const directionsService = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      map: map,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: "#f59e0b",
        strokeWeight: 4,
      },
    });
    
    directionsService.route(
      {
        origin: { lat: pickupCoords.lat, lng: pickupCoords.lng },
        destination: { lat: destCoords.lat, lng: destCoords.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") {
          directionsRenderer.setDirections(result);
        }
      }
    );
    
    setMapLoaded(true);
  }, [pickupCoords, destCoords, destination, bookingType]);

  const handleSubmit = async () => {
    if (!customerName || !customerEmail || !pickupLocation || !pickupDate || !pickupTime) {
      toast.error('Please fill in all required fields (name, email, pickup, date, time)');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (bookingType === 'tour' && hours < 3) {
      toast.error('Tours require minimum 3 hours');
      return;
    }
    
    // Validate round-trip fields if enabled
    if (isRoundTrip && bookingType === 'transfer') {
      if (!returnPickupLocation || !returnDestination || !returnDate || !returnTime) {
        toast.error('Please fill in all return trip fields');
        return;
      }
    }

    setLoading(true);
    try {
      const bookingData = {
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone || null,
        booking_type: bookingType,
        vehicle_type: selectedVehicle,
        pickup_location: pickupLocation,
        pickup_lat: pickupCoords.lat,
        pickup_lng: pickupCoords.lng,
        destination: destination || null,
        destination_lat: destCoords.lat || null,
        destination_lng: destCoords.lng || null,
        pickup_datetime: `${pickupDate}T${pickupTime}`,
        passengers: passengers,
        hours: bookingType === 'tour' ? hours : null,
        notes: notes || null,
        payment_type: paymentType,  // 'deposit' or 'full'
        is_round_trip: isRoundTrip && bookingType === 'transfer',
        promo_code: promoCode || null,
      };
      
      // Add return trip data if round-trip is enabled
      if (isRoundTrip && bookingType === 'transfer') {
        bookingData.return_pickup_location = returnPickupLocation;
        bookingData.return_pickup_lat = returnPickupCoords.lat;
        bookingData.return_pickup_lng = returnPickupCoords.lng;
        bookingData.return_destination = returnDestination;
        bookingData.return_destination_lat = returnDestCoords.lat;
        bookingData.return_destination_lng = returnDestCoords.lng;
        bookingData.return_datetime = `${returnDate}T${returnTime}`;
      }
      
      const response = await axios.post(`${API}/bookings/public`, bookingData);
      
      const booking = response.data;
      toast.success('Booking created! Redirecting to payment...');
      
      // Redirect to embedded payment page
      navigate(`/payment?booking_id=${booking.booking_id}&payment_type=${paymentType}`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handlePickupSearch = (value) => {
    setPickupLocation(value);
    if (pickupTimeout.current) clearTimeout(pickupTimeout.current);
    
    if (value.length >= 2) {
      setSearchingPickup(true);
      setShowPickupSuggestions(true);
      pickupTimeout.current = setTimeout(async () => {
        // Always use OpenStreetMap for reliability
        const results = await searchLocations(value);
        setPickupSuggestions(results);
        setSearchingPickup(false);
      }, 300);
    } else {
      setShowPickupSuggestions(false);
    }
  };

  const handleDestSearch = (value) => {
    setDestination(value);
    if (destTimeout.current) clearTimeout(destTimeout.current);
    
    if (value.length >= 2) {
      setSearchingDest(true);
      setShowDestSuggestions(true);
      destTimeout.current = setTimeout(async () => {
        // Always use OpenStreetMap for reliability
        const results = await searchLocations(value);
        setDestSuggestions(results);
        setSearchingDest(false);
      }, 300);
    } else {
      setShowDestSuggestions(false);
    }
  };

  const selectPickup = (loc) => {
    setPickupLocation(loc.name);
    
    // If using Google Maps, get coordinates from Place ID
    if (loc.placeId && window.google) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails({ placeId: loc.placeId }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setPickupCoords({ 
            lat: place.geometry.location.lat(), 
            lng: place.geometry.location.lng() 
          });
        }
      });
    } else if (loc.lat && loc.lng) {
      // OpenStreetMap fallback
      setPickupCoords({ lat: loc.lat, lng: loc.lng });
    }
    
    setShowPickupSuggestions(false);
  };

  const selectDest = (loc) => {
    setDestination(loc.name);
    
    // If using Google Maps, get coordinates from Place ID
    if (loc.placeId && window.google) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails({ placeId: loc.placeId }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setDestCoords({ 
            lat: place.geometry.location.lat(), 
            lng: place.geometry.location.lng() 
          });
        }
      });
    } else if (loc.lat && loc.lng) {
      // OpenStreetMap fallback
      setDestCoords({ lat: loc.lat, lng: loc.lng });
    }
    
    setShowDestSuggestions(false);
  };

  // Return trip search handlers
  const handleReturnPickupSearch = (value) => {
    setReturnPickupLocation(value);
    if (returnPickupTimeout.current) clearTimeout(returnPickupTimeout.current);
    
    if (value.length >= 2) {
      setSearchingReturnPickup(true);
      setShowReturnPickupSuggestions(true);
      returnPickupTimeout.current = setTimeout(async () => {
        // Always use OpenStreetMap for reliability
        const results = await searchLocations(value);
        setReturnPickupSuggestions(results);
        setSearchingReturnPickup(false);
      }, 300);
    } else {
      setShowReturnPickupSuggestions(false);
    }
  };

  const handleReturnDestSearch = (value) => {
    setReturnDestination(value);
    if (returnDestTimeout.current) clearTimeout(returnDestTimeout.current);
    
    if (value.length >= 2) {
      setSearchingReturnDest(true);
      setShowReturnDestSuggestions(true);
      returnDestTimeout.current = setTimeout(async () => {
        // Always use OpenStreetMap for reliability
        const results = await searchLocations(value);
        setReturnDestSuggestions(results);
        setSearchingReturnDest(false);
      }, 300);
    } else {
      setShowReturnDestSuggestions(false);
    }
  };

  const selectReturnPickup = (loc) => {
    setReturnPickupLocation(loc.name);
    
    if (loc.placeId && window.google) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails({ placeId: loc.placeId }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setReturnPickupCoords({ 
            lat: place.geometry.location.lat(), 
            lng: place.geometry.location.lng() 
          });
        }
      });
    } else if (loc.lat && loc.lng) {
      setReturnPickupCoords({ lat: loc.lat, lng: loc.lng });
    }
    
    setShowReturnPickupSuggestions(false);
  };

  const selectReturnDest = (loc) => {
    setReturnDestination(loc.name);
    
    if (loc.placeId && window.google) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails({ placeId: loc.placeId }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setReturnDestCoords({ 
            lat: place.geometry.location.lat(), 
            lng: place.geometry.location.lng() 
          });
        }
      });
    } else if (loc.lat && loc.lng) {
      setReturnDestCoords({ lat: loc.lat, lng: loc.lng });
    }
    
    setShowReturnDestSuggestions(false);
  };

  const calculateEstimate = async () => {
    const vehicle = VEHICLES.find(v => v.type === selectedVehicle);
    if (!vehicle) return 0;

    let basePrice = 0;

    if (bookingType === 'tour') {
      const h = hours < 3 ? 3 : hours;  // Minimum 3 hours
      basePrice = vehicle.tourRate * h;
    } else {
      // Transfer pricing - flat rate around Santorini
      const flat = passengers <= 4 ? 38 : 50;
      basePrice = flat;

      // If round-trip, double it (return leg same flat rate)
      if (isRoundTrip) {
        basePrice += flat;
      }
    }

    // Apply discounts
    let finalPrice = basePrice;
    
    // 10% discount for early booking (30+ days in advance)
    if (pickupDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const bookingDate = new Date(pickupDate);
      const daysUntilBooking = Math.floor((bookingDate - today) / (1000 * 60 * 60 * 24));
      
      if (daysUntilBooking >= 30) {
        finalPrice = finalPrice * 0.9; // 10% discount
      }
    }
    
    // 10% discount for round-trip
    if (isRoundTrip && bookingType === 'transfer') {
      finalPrice = finalPrice * 0.9; // 10% discount
    }

    // Promo code discount (e.g. SAVE5 from exit-intent popup)
    if (promoCode === 'SAVE5') {
      finalPrice = finalPrice * 0.95; // 5% extra off
    }

    return finalPrice;
  };

  const resetForm = () => {
    setStep(isTourOnly ? 2 : 1); // Reset to appropriate starting step
    setBookingType(isTourOnly ? 'tour' : 'transfer');
    setSelectedVehicle('van');
    setPassengers(1);
    setPickupLocation('');
    setDestination('');
    setPickupDate('');
    setPickupTime('');
    setHours(3);
    setNotes('');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] pb-16 md:pb-0">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FAFAF7]/90 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_santorini-booking/artifacts/b01x660c_FullLogo%20%281%29.png" 
                alt="Santorini Best Experience"
                className="h-12 md:h-14"
              />
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-2">
              <div className="px-4 py-2 rounded-xl bg-[#B8860B]/10 text-[#B8860B] border border-[#0A2540]/30 text-sm font-medium">
                {t('nav.bookNow')}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900">
                  {t('nav.home')}
                </Button>
              </Link>
              
              <button className="md:hidden text-slate-600" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="animate-fade-in">
            {/* Step 1: Choose Booking Type (only shown when not coming from tour link) */}
            {step === 1 && !isTourOnly && (
              <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('booking.title')}</h1>
                <p className="text-slate-600 mb-8">Select the type of service you need</p>
                
                <div className="space-y-4 mb-8">
                  <button
                    onClick={() => {
                      setBookingType('transfer');
                      setStep(2);
                    }}
                    className="w-full p-6 rounded-xl border-2 border-slate-200 bg-white hover:border-slate-200 transition-all text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                        <Plane className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="text-slate-900 font-semibold text-lg">{t('quickBooking.airportTransfer')}</h3>
                        <p className="text-slate-600">Point-to-point transportation</p>
                        <p className="text-slate-500 text-sm mt-1">{t('quickBooking.from')} €38 flat rate</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      setBookingType('tour');
                      setStep(2);
                    }}
                    className="w-full p-6 rounded-xl border-2 border-slate-200 bg-white hover:border-slate-200 transition-all text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[#B8860B]/10 flex items-center justify-center">
                        <Sun className="w-6 h-6 text-[#B8860B]" />
                      </div>
                      <div>
                        <h3 className="text-slate-900 font-semibold text-lg">{t('quickBooking.sunsetExperience')}</h3>
                        <p className="text-slate-600">Explore the island with a local driver</p>
                        <p className="text-slate-500 text-sm mt-1">{t('quickBooking.from')} €65/hour (min 3 hours)</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Choose Vehicle */}
              {step === 2 && (
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('booking.chooseVehicle')}</h1>
                  <p className="text-slate-600 mb-8">
                    {bookingType === 'tour' 
                      ? t('booking.selectVehicleTour')
                      : t('booking.selectVehicleTransfer')
                    }
                  </p>
                  
                  <div className="space-y-4 mb-8">
                    {VEHICLES.map((vehicle) => (
                      <button
                        key={vehicle.type}
                        onClick={() => setSelectedVehicle(vehicle.type)}
                        className={`w-full p-5 rounded-xl border-2 transition-all text-left flex items-center justify-between ${
                          selectedVehicle === vehicle.type
                            ? 'border-[#0A2540] bg-[#B8860B]/10'
                            : 'border-slate-200 bg-white hover:border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-12 rounded-lg overflow-hidden bg-slate-50">
                            <img 
                              src={vehicle.image} 
                              alt={vehicle.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <h3 className="text-slate-900 font-semibold">{vehicle.name}</h3>
                            <p className="text-slate-500 text-sm">{vehicle.desc}</p>
                            <p className="text-slate-600 text-sm mt-1">
                              <Users className="w-4 h-4 inline mr-1" />
                              {t('booking.upTo')} {vehicle.passengers} {t('booking.passengersLabel')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#B8860B]">
                            €{bookingType === 'tour' ? vehicle.tourRate : (passengers <= 4 ? 38 : 50)}
                          </p>
                          <p className="text-slate-500 text-sm">
                            {bookingType === 'tour' ? t('booking.perHourRate') : (passengers <= 4 ? '1-4 pax flat' : '5-8 pax flat')}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  <div className="mb-6">
                    <Label className="text-slate-700 mb-2 block">{t('booking.numberOfPassengers')}</Label>
                    <div className="flex items-center gap-4">
                      <Button 
                        type="button"
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPassengers(Math.max(1, passengers - 1));
                        }}
                        className="border-slate-200"
                        data-testid="passengers-decrement"
                      >
                        -
                      </Button>
                      <span className="text-slate-900 text-xl font-semibold w-8 text-center" data-testid="passengers-count">{passengers}</span>
                      <Button 
                        type="button"
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPassengers(Math.min(8, passengers + 1));
                        }}
                        className="border-slate-200"
                        data-testid="passengers-increment"
                      >
                        +
                      </Button>
                    </div>
                    {passengers >= 8 && (
                      <div className="mt-4 rounded-xl border border-green-500/30 bg-green-500/10 p-4 flex items-start gap-3" data-testid="group-booking-hint">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#25D366" className="w-5 h-5 shrink-0 mt-0.5">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347" />
                        </svg>
                        <div className="flex-1 text-sm">
                          <p className="text-slate-900 font-semibold mb-1">Group of 9 or more?</p>
                          <p className="text-slate-700 mb-2">Our Van fits up to 8. For larger groups we arrange multiple vehicles — contact us for a custom quote.</p>
                          <a
                            href="https://wa.me/306974994577?text=Hi!%20I%20need%20a%20transfer%20in%20Santorini%20for%20more%20than%208%20people.%20Can%20you%20send%20a%20quote%3F"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 font-medium"
                            data-testid="group-booking-whatsapp"
                          >
                            Chat with us on WhatsApp →
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Button onClick={() => setStep(3)} className="w-full h-14 bg-[#0A2540] hover:bg-[#153554] text-white font-medium tracking-wider uppercase rounded-full">
                    {t('booking.continue')} <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              )}

              {/* Step 3: Customer Info & Details */}
              {step === 3 && (
                <div>
                  <button onClick={() => setStep(2)} className="text-[#B8860B] mb-4 flex items-center gap-1">
                    ← {t('booking.back')}
                  </button>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('booking.yourDetails')}</h1>
                  <p className="text-slate-600 mb-8">{t('booking.contactConfirm')}</p>
                  
                  <div className="space-y-6 mb-8">
                    {/* Customer Name */}
                    <div>
                      <Label className="text-slate-700 mb-2 block">{t('booking.fullName')} *</Label>
                      <Input
                        placeholder={t('booking.fullNamePlaceholder')}
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="h-12 bg-slate-50 border-slate-200 text-slate-900"
                      />
                    </div>
                    
                    {/* Customer Email */}
                    <div>
                      <Label className="text-slate-700 mb-2 block">{t('booking.email')} *</Label>
                      <Input
                        type="email"
                        placeholder={t('booking.emailPlaceholder')}
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        className="h-12 bg-slate-50 border-slate-200 text-slate-900"
                      />
                    </div>
                    
                    {/* Customer Phone (Optional) */}
                    <div>
                      <Label className="text-slate-700 mb-2 block">{t('booking.phone')}</Label>
                      <Input
                        type="tel"
                        placeholder={t('booking.phonePlaceholder')}
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        className="h-12 bg-slate-50 border-slate-200 text-slate-900"
                      />
                    </div>
                    
                    {/* Pickup Location */}
                    <div className="relative">
                      <Label className="text-slate-700 mb-2 block">{t('booking.pickupLocation')} *</Label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#B8860B]" />
                        <Input
                          placeholder={t('booking.searchPlaceholder')}
                          value={pickupLocation}
                          onChange={(e) => handlePickupSearch(e.target.value)}
                          className="pl-12 h-12 bg-slate-50 border-slate-200 text-slate-900"
                        />
                      </div>
                      {showPickupSuggestions && (
                        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-slate-50 border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                          {searchingPickup ? (
                            <div className="px-4 py-3 text-slate-600 flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" /> {t('booking.searching')}
                            </div>
                          ) : pickupSuggestions.length > 0 ? (
                            pickupSuggestions.map((loc, i) => (
                              <button key={i} onClick={() => selectPickup(loc)}
                                className="w-full text-left px-4 py-3 hover:bg-slate-200 text-slate-900">
                                {loc.name}
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-slate-500">{t('booking.noResults')}</div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Destination (for transfers) */}
                    {bookingType === 'transfer' && (
                      <div className="relative">
                        <Label className="text-slate-700 mb-2 block">{t('booking.dropoffLocation')}</Label>
                        <div className="relative">
                          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400" />
                          <Input
                            placeholder={t('booking.destinationPlaceholder')}
                            value={destination}
                            onChange={(e) => handleDestSearch(e.target.value)}
                            className="pl-12 h-12 bg-slate-50 border-slate-200 text-slate-900"
                          />
                        </div>
                        {showDestSuggestions && (
                          <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-slate-50 border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                            {searchingDest ? (
                              <div className="px-4 py-3 text-slate-600 flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" /> {t('booking.searching')}
                              </div>
                            ) : destSuggestions.length > 0 ? (
                              destSuggestions.map((loc, i) => (
                                <button key={i} onClick={() => selectDest(loc)}
                                  className="w-full text-left px-4 py-3 hover:bg-slate-200 text-slate-900">
                                  {loc.name}
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-slate-500">{t('booking.noResults')}</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Round-Trip Toggle (for transfers only) */}
                    {bookingType === 'transfer' && (
                      <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isRoundTrip}
                            onChange={(e) => setIsRoundTrip(e.target.checked)}
                            className="w-5 h-5 rounded border-green-500 text-green-500 focus:ring-green-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-900 font-semibold">Round-Trip Booking</span>
                              <span className="px-2 py-0.5 bg-green-500 text-slate-900 text-xs font-bold rounded">10% OFF</span>
                            </div>
                            <p className="text-slate-600 text-sm mt-1">Book both arrival and departure transfers together and save 10%</p>
                          </div>
                        </label>
                      </div>
                    )}
                    
                    {/* Return Trip Fields (shown when round-trip is enabled) */}
                    {bookingType === 'transfer' && isRoundTrip && (
                      <div className="space-y-6 p-5 bg-white/50 border border-slate-200 rounded-xl">
                        <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                          <ArrowRight className="w-5 h-5 text-green-400 rotate-180" />
                          Return Trip Details
                        </h4>
                        
                        {/* Return Pickup Location */}
                        <div className="relative">
                          <Label className="text-slate-700 mb-2 block">Return Pickup Location *</Label>
                          <div className="relative">
                            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#B8860B]" />
                            <Input
                              placeholder="Enter return pickup location..."
                              value={returnPickupLocation}
                              onChange={(e) => handleReturnPickupSearch(e.target.value)}
                              className="pl-12 h-12 bg-slate-50 border-slate-200 text-slate-900"
                            />
                          </div>
                          {showReturnPickupSuggestions && (
                            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-slate-50 border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                              {searchingReturnPickup ? (
                                <div className="px-4 py-3 text-slate-600 flex items-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin" /> Searching...
                                </div>
                              ) : returnPickupSuggestions.length > 0 ? (
                                returnPickupSuggestions.map((loc, i) => (
                                  <button key={i} onClick={() => selectReturnPickup(loc)}
                                    className="w-full text-left px-4 py-3 hover:bg-slate-200 text-slate-900">
                                    {loc.name}
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-slate-500">No results found</div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Return Destination */}
                        <div className="relative">
                          <Label className="text-slate-700 mb-2 block">Return Destination *</Label>
                          <div className="relative">
                            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400" />
                            <Input
                              placeholder="Enter return destination..."
                              value={returnDestination}
                              onChange={(e) => handleReturnDestSearch(e.target.value)}
                              className="pl-12 h-12 bg-slate-50 border-slate-200 text-slate-900"
                            />
                          </div>
                          {showReturnDestSuggestions && (
                            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-slate-50 border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                              {searchingReturnDest ? (
                                <div className="px-4 py-3 text-slate-600 flex items-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin" /> Searching...
                                </div>
                              ) : returnDestSuggestions.length > 0 ? (
                                returnDestSuggestions.map((loc, i) => (
                                  <button key={i} onClick={() => selectReturnDest(loc)}
                                    className="w-full text-left px-4 py-3 hover:bg-slate-200 text-slate-900">
                                    {loc.name}
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-slate-500">No results found</div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Return Date & Time */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-700 mb-2 block">Return Date *</Label>
                            <div className="relative">
                              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                              <Input
                                type="date"
                                value={returnDate}
                                onChange={(e) => setReturnDate(e.target.value)}
                                min={pickupDate || new Date().toISOString().split('T')[0]}
                                className="pl-12 h-12 bg-slate-50 border-slate-200 text-slate-900"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-slate-700 mb-2 block">Return Time *</Label>
                            <div className="relative">
                              <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                              <Input
                                type="time"
                                value={returnTime}
                                onChange={(e) => setReturnTime(e.target.value)}
                                className="pl-12 h-12 bg-slate-50 border-slate-200 text-slate-900"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Hours (for tour booking) */}
                    {bookingType === 'tour' && (
                      <div>
                        <Label className="text-slate-700 mb-2 block">{t('booking.duration')}</Label>
                        <div className="flex items-center gap-4">
                          <Button variant="outline" size="sm" onClick={() => setHours(Math.max(3, hours - 1))}
                            className="border-slate-200">-</Button>
                          <span className="text-slate-900 text-xl font-semibold w-8 text-center">{hours}</span>
                          <Button variant="outline" size="sm" onClick={() => setHours(Math.min(12, hours + 1))}
                            className="border-slate-200">+</Button>
                          <span className="text-slate-500">{t('booking.hoursLabel')}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Date & Time */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700 mb-2 block">{t('booking.date')} *</Label>
                        <div className="relative">
                          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                          <Input
                            type="date"
                            value={pickupDate}
                            onChange={(e) => setPickupDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="pl-12 h-12 bg-slate-50 border-slate-200 text-slate-900"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-700 mb-2 block">{t('booking.time')} *</Label>
                        <div className="relative">
                          <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                          <Input
                            type="time"
                            value={pickupTime}
                            onChange={(e) => setPickupTime(e.target.value)}
                            className="pl-12 h-12 bg-slate-50 border-slate-200 text-slate-900"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Notes */}
                    <div>
                      <Label className="text-slate-700 mb-2 block">Special Requests (optional)</Label>
                      <textarea
                        placeholder="Flight number, child seats, special requirements..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full h-24 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-500 resize-none"
                      />
                    </div>
                    
                    {/* Interactive Map (for transfers with both locations) - TEMPORARILY DISABLED */}
                    {/* Uncomment when Google Maps API is fully configured
                    {bookingType === 'transfer' && pickupCoords.lat && destCoords.lat && destination && (
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <h4 className="text-slate-900 font-semibold mb-3 flex items-center gap-2">
                          <Map className="w-5 h-5 text-[#B8860B]" />
                          Your Route
                        </h4>
                        <div 
                          id="booking-map" 
                          ref={mapRef}
                          className="w-full h-64 rounded-lg overflow-hidden bg-slate-50"
                        />
                      </div>
                    )}
                    */}
                  </div>
                  
                  {/* Price Summary */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-slate-600">Vehicle</span>
                      <span className="text-slate-900">{VEHICLES.find(v => v.type === selectedVehicle)?.name}</span>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-slate-600">Passengers</span>
                      <span className="text-slate-900">{passengers}</span>
                    </div>
                    {bookingType === 'tour' && (
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-slate-600">Duration</span>
                        <span className="text-slate-900">{hours} hours</span>
                      </div>
                    )}
                    
                    {/* Show Active Discounts */}
                    {(() => {
                      const earlyBookingDiscount = pickupDate && (() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const bookingDate = new Date(pickupDate);
                        const daysUntilBooking = Math.floor((bookingDate - today) / (1000 * 60 * 60 * 24));
                        return daysUntilBooking >= 30;
                      })();
                      
                      const hasDiscount = earlyBookingDiscount || (isRoundTrip && bookingType === 'transfer') || promoCode === 'SAVE5';
                      
                      return hasDiscount ? (
                        <div className="mb-4 space-y-2">
                          {earlyBookingDiscount && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-green-400 flex items-center gap-1">
                                <Check className="w-4 h-4" /> Early Booking Discount
                              </span>
                              <span className="text-green-400 font-semibold">-10%</span>
                            </div>
                          )}
                          {isRoundTrip && bookingType === 'transfer' && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-green-400 flex items-center gap-1">
                                <Check className="w-4 h-4" /> Round-Trip Discount
                              </span>
                              <span className="text-green-400 font-semibold">-10%</span>
                            </div>
                          )}
                          {promoCode === 'SAVE5' && (
                            <div className="flex items-center justify-between text-sm" data-testid="promo-discount-row">
                              <span className="text-[#B8860B] flex items-center gap-1">
                                <Check className="w-4 h-4" /> Promo {promoCode}
                              </span>
                              <span className="text-[#B8860B] font-semibold">-5%</span>
                            </div>
                          )}
                        </div>
                      ) : null;
                    })()}
                    
                    <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-200">
                      <span className="text-slate-600">Total Service Cost</span>
                      <span className="text-slate-900 font-semibold">€{estimatedPrice.toFixed(2)}</span>
                    </div>
                    
                    {/* Payment Option Selector */}
                    <div className="mb-4">
                      <Label className="text-slate-700 mb-3 block">Payment Option</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setPaymentType('deposit')}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            paymentType === 'deposit' 
                              ? 'border-[#0A2540] bg-[#B8860B]/10' 
                              : 'border-slate-200 bg-slate-50/50 hover:border-slate-400'
                          }`}
                        >
                          <div className="text-left">
                            <div className={`font-semibold mb-1 ${paymentType === 'deposit' ? 'text-[#B8860B]' : 'text-slate-900'}`}>
                              10% Deposit
                            </div>
                            <div className="text-2xl font-bold text-slate-900 mb-1">
                              €{(estimatedPrice * 0.1).toFixed(2)}
                            </div>
                            <div className="text-xs text-slate-500">
                              Pay remaining to driver
                            </div>
                          </div>
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => setPaymentType('full')}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            paymentType === 'full' 
                              ? 'border-green-500 bg-green-500/10' 
                              : 'border-slate-200 bg-slate-50/50 hover:border-slate-400'
                          }`}
                        >
                          <div className="text-left">
                            <div className={`font-semibold mb-1 ${paymentType === 'full' ? 'text-green-400' : 'text-slate-900'}`}>
                              Full Payment
                            </div>
                            <div className="text-2xl font-bold text-slate-900 mb-1">
                              €{estimatedPrice.toFixed(2)}
                            </div>
                            <div className="text-xs text-slate-500">
                              Pay everything now
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                    
                    {paymentType === 'deposit' && (
                      <div className="bg-[#B8860B]/10 border border-[#0A2540]/30 rounded-lg p-3 mb-2">
                        <p className="text-xs text-amber-200">
                          💡 You&apos;ll pay €{(estimatedPrice * 0.9).toFixed(2)} to the driver after service
                        </p>
                      </div>
                    )}
                    {paymentType === 'full' && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-2">
                        <p className="text-xs text-green-200">
                          ✓ Complete payment now - nothing to pay later
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading}
                    className={`w-full h-14 font-medium text-base tracking-wider uppercase rounded-full ${
                      paymentType === 'full'
                        ? 'bg-emerald-700 hover:bg-emerald-800'
                        : 'bg-[#0A2540] hover:bg-[#153554]'
                    } text-white`}
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <>
                        {paymentType === 'deposit' 
                          ? `Pay Deposit €${(estimatedPrice * 0.1).toFixed(2)}`
                          : `Pay Full Amount €${estimatedPrice.toFixed(2)}`
                        } <Check className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }
